import React from 'react';
import { Tooltip } from '@plerionhq/design-system';

import { useTranslation } from 'react-i18next';

export interface TokenUsage {
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  cacheReadInputTokens: number;
  cacheWriteInputTokens: number;
  promptCost: number;
  completionCost: number;
  cacheReadInputCost: number;
  cacheWriteInputCost: number;
  totalCost: number;
  savings: number;
}

interface TokenUsageProps {
  tokenUsage: TokenUsage;
  className?: string;
}

export const TokenUsageDisplay: React.FC<TokenUsageProps> = ({
  tokenUsage,
  className = '',
}) => {
  const { t } = useTranslation('pleri/chat');
  if (!tokenUsage) {
    return null;
  }

  // Format cost as string with 4 decimal places
  const formatCost = (cost: number | undefined) => (cost ?? 0).toFixed(4);

  return (
    <div className={`inline-flex items-center text-xs ${className}`}>
      <div className="flex flex-wrap items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-2 py-1 dark:border-gray-700 dark:bg-gray-800">
        <Tooltip label={t('inputTokensAndCost')}>
          <div className="flex items-center gap-1.5">
            <span className="font-medium text-gray-600 dark:text-gray-400">
              {t('in')}
            </span>
            <span className="font-mono text-gray-700 dark:text-gray-300">
              {tokenUsage.promptTokens.toLocaleString()}
            </span>
            <span className="mx-0.5 text-gray-400 dark:text-gray-500">·</span>
            <span className="font-mono text-gray-600 dark:text-gray-400">
              ${formatCost(tokenUsage.promptCost)}
            </span>
          </div>
        </Tooltip>

        {tokenUsage.cacheReadInputTokens > 0 && (
          <>
            <span className="text-gray-300">|</span>
            <Tooltip label={t('cacheReadTokensAndCost')}>
              <div className="flex items-center gap-1.5">
                <span className="font-medium text-gray-600 dark:text-gray-400">
                  {t('cacheRead')}
                </span>
                <span className="font-mono text-gray-700 dark:text-gray-300">
                  {tokenUsage.cacheReadInputTokens.toLocaleString()}
                </span>
                <span className="mx-0.5 text-gray-400 dark:text-gray-500">
                  ·
                </span>
                <span className="font-mono text-blue-600">
                  ${formatCost(tokenUsage.cacheReadInputCost)}
                </span>
              </div>
            </Tooltip>
          </>
        )}

        {tokenUsage.cacheWriteInputTokens > 0 && (
          <>
            <span className="text-gray-300">|</span>
            <Tooltip label={t('cacheWriteTokensAndCost')}>
              <div className="flex items-center gap-1.5">
                <span className="font-medium text-gray-600 dark:text-gray-400">
                  {t('cacheWrite')}
                </span>
                <span className="font-mono text-gray-700 dark:text-gray-300">
                  {tokenUsage.cacheWriteInputTokens.toLocaleString()}
                </span>
                <span className="mx-0.5 text-gray-400 dark:text-gray-500">
                  ·
                </span>
                <span className="font-mono text-purple-600">
                  ${formatCost(tokenUsage.cacheWriteInputCost)}
                </span>
              </div>
            </Tooltip>
          </>
        )}

        <span className="text-gray-300">|</span>

        <Tooltip label={t('outputTokensAndCost')}>
          <div className="flex items-center gap-1.5">
            <span className="font-medium text-gray-600 dark:text-gray-400">
              {t('out')}
            </span>
            <span className="font-mono text-gray-700 dark:text-gray-300">
              {tokenUsage.completionTokens.toLocaleString()}
            </span>
            <span className="mx-0.5 text-gray-400 dark:text-gray-500">·</span>
            <span className="font-mono text-gray-600 dark:text-gray-400">
              ${formatCost(tokenUsage.completionCost)}
            </span>
          </div>
        </Tooltip>

        <span className="text-gray-300">|</span>

        <Tooltip label={t('totalTokensAndCost')}>
          <div className="flex items-center gap-1.5">
            <span className="font-medium text-gray-600 dark:text-gray-400">
              {t('total')}
            </span>
            <span className="font-mono text-gray-700 dark:text-gray-300">
              {tokenUsage.totalTokens.toLocaleString()}
            </span>
            <span className="mx-0.5 text-gray-400 dark:text-gray-500">·</span>
            <span className="font-mono font-semibold text-green-600">
              ${formatCost(tokenUsage.totalCost)}
            </span>
          </div>
        </Tooltip>

        <span className="text-gray-300">|</span>
        <Tooltip label={t('savings')}>
          <div className="flex items-center gap-1.5">
            <span className="font-medium text-gray-600 dark:text-gray-400">
              {t('savingsLabel')}
            </span>
            <span className="mx-0.5 text-gray-400 dark:text-gray-500">·</span>
            <span className="font-mono text-green-600">
              ${formatCost(tokenUsage.savings)}
            </span>
          </div>
        </Tooltip>
      </div>
    </div>
  );
};
